import { NavLink, useLocation } from 'react-router-dom';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import {
  auth as authApi,
  messages as messagesApi,
  users as usersApi,
} from '../../api/endpoints';
import { watchActiviteNeuves } from '../../firebase/activite';
import { poserLaPastille } from '../../native/pastille';
import { useSession } from '../../store/SessionContext';
import { useTabNav } from './TabNav';
import { useOnglets } from './tabs';
import { sensX, t } from '../../i18n';

/**
 * Barre d'onglets, cinq entrees maximum.
 *
 * L'onglet actif passe son icone en `fill`, comme le prevoit le systeme
 * d'icones. La couleur du spot, elle, n'est plus posee sur la case
 * active : elle vit dans une pastille unique qui glisse de l'une a
 * l'autre. Deux fonds qui s'allument et s'eteignent donnaient un saut ;
 * une seule forme qui se deplace dit d'ou l'on vient.
 *
 * Les parametres ne vivent pas ici : ils s'ouvrent depuis l'en-tete du
 * profil.
 */

/**
 * La barre appartient a l'ecran qu'on regarde, pas a l'adresse courante.
 *
 * Pendant un retour au doigt, l'ecran d'arrivee est deja pose derriere et
 * se decouvre au fur et a mesure — mais l'adresse, elle, ne change qu'une
 * fois le repli termine, un quart de seconde plus tard. Se fier a elle
 * faisait donc apparaitre la barre apres coup, alors que la liste des
 * conversations occupait deja tout l'ecran. On regarde donc ou mene le
 * geste quand il y en a un.
 *
 * Rien a craindre de la voir trop tot : tant qu'il reste de l'ecran a
 * decouvrir, la conversation passe par-dessus (voir `screen-back--held`
 * dans la feuille de style). La barre se devoile avec le reste.
 */
export function TabBar() {
  const location = useLocation();
  const { backPeek } = useTabNav();
  const { user } = useSession();
  const { chemins } = useOnglets();

  /*
    La barre est montee hors des routes, et ne lisait que l'adresse. Or
    une adresse d'onglet ne prouve pas que l'onglet s'affiche : tant que
    l'adresse n'est pas confirmee, `RequireSpot` remplace le contenu par
    la porte de confirmation sans que l'adresse change. La barre se
    dessinait alors par-dessus, un onglet allume, donnant a croire que
    l'application etait ouverte alors qu'elle ne l'etait pas.
  */
  if (!user || !authApi.isVerified()) return null;
  /*
    Le peek porte la recherche avec lui — `/app/feed?q=rooftop` est un
    etat a part entiere du fil — la ou les onglets sont des chemins nus.
    Sans la couper, revenir d'un post vers un fil filtre laisserait la
    barre absente.
  */
  const chemin = (backPeek ?? location.pathname).split('?')[0];

  if (!chemins.includes(chemin)) return null;
  return <Barre chemin={chemin} />;
}

/**
 * Remonter l'onglet qu'on regarde.
 *
 * Toucher le symbole d'un onglet ou l'on est deja ne menait nulle part :
 * l'adresse ne changeait pas, donc rien ne se passait. C'est pourtant la
 * qu'on appuie apres avoir defile un moment, et tous les telephones
 * repondent la meme chose — on remonte.
 *
 * Toutes les zones defilantes de la bande visible, et pas la premiere
 * venue : un ecran peut en porter deux. Celles qui sont deja en haut ne
 * bougent pas, cela ne coute rien.
 */
function remonterLOnglet(): void {
  const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const zones = document.querySelectorAll<HTMLElement>('.tab-swipe__pane .scroll-area');
  for (const zone of zones) {
    zone.scrollTo({ top: 0, behavior: doux ? 'smooth' : 'auto' });
  }
}

function Barre({ chemin }: { chemin: string }) {
  const emplacement = useLocation();
  /*
    La pastille dit qu'il y a quelque chose a regarder dans les messages.

    Trois choses, en fait : des messages non lus, une demande d'ami, une
    demande de message. Elles vivent derriere le meme onglet, et un point
    par sorte de nouveaute aurait fait trois points sur un dessin de
    vingt-six pixels. Un seul suffit — il dit « va voir », le detail est
    de l'autre cote.

    Elle se relit a chaque changement d'onglet — ouvrir un fil cale le
    repere de lecture, ouvrir une boite eteint la sienne — et surtout des
    que quelque chose arrive, sans qu'on ait a changer d'ecran. Sans cela,
    un message recu pendant qu'on lit le feed n'allumait rien : les fils
    etaient bien en temps reel, mais rien ne disait d'aller les voir.
  */
  const [nonLus, setNonLus] = useState(0);
  const [demandes, setDemandes] = useState(0);
  const aVoir = nonLus + demandes;

  /*
    L'autre pastille.

    Le fil en a une des qu'une reaction attend dans la cloche. Elle ne
    porte pas de nombre : un onglet dit « va voir », pas « va voir trois
    fois » — le detail est de l'autre cote, sur la cloche.
  */
  const [activite, setActivite] = useState(0);

  useEffect(() => {
    try {
      return watchActiviteNeuves(setActivite);
    } catch {
      return undefined;
    }
  }, []);

  /*
    L'icone de l'application dit la meme chose que les pastilles d'ici.

    Le serveur la peint en envoyant ses notifications, mais rien ne
    l'efface au retour : on lit tout, et l'icone annonce encore trois
    choses qui n'attendent plus. La barre d'onglets connait les deux
    comptes a la seconde pres — c'est donc elle qui remet le systeme
    d'accord.
  */
  useEffect(() => {
    void poserLaPastille(nonLus + activite);
  }, [nonLus, activite]);

  /*
    Les messages qui attendent : le serveur tient le compte, on l'ecoute.
    Quelques nombres dans une seule branche, et rien a recalculer.
  */
  useEffect(() => {
    // Une barre d'onglets ne casse pas pour un decompte : sans session,
    // il n'y a rien a ecouter et elle reste eteinte.
    try {
      return messagesApi.watchNonLus(setNonLus);
    } catch {
      return undefined;
    }
  }, []);

  /*
    Les demandes d'ami, elles, ne vivent pas dans les fils : c'est la
    seule source qu'il faut ecouter a part. Le recompte qu'elle declenche
    ne lit qu'une branche, et l'ouverture d'un ecran la rafraichit aussi —
    consulter sa boite eteint sa pastille.
  */
  const recompterDemandes = useCallback(async () => {
    try {
      setDemandes(await usersApi.newRequestCount());
    } catch {
      setDemandes(0);
    }
  }, []);

  useEffect(() => {
    void recompterDemandes();
  }, [chemin, recompterDemandes]);

  useEffect(() => {
    try {
      return usersApi.watchRequests(() => void recompterDemandes());
    } catch {
      return undefined;
    }
  }, [recompterDemandes]);

  const { onglets, chemins } = useOnglets();
  const active = chemins.indexOf(chemin);
  const nav = useTabNav();

  /*
    La barre se prend au doigt, n'importe ou.

    Poser le doigt sur la rangee puis glisser fait avancer les ecrans
    comme un balayage : c'est le meme geste, pris par l'autre bout.

    **Depuis n'importe quelle icone, et non plus seulement la pastille.**
    On tenait le geste a l'onglet courant, ce qui obligeait a viser la
    pastille avant de se promener : poser le doigt sur Messages depuis
    Trophees ne faisait rien. Le doigt commande maintenant en absolu — la
    pastille vient sous lui et le suit — ce qui est la seule lecture
    naturelle de « se balader dans la barre ».

    Le doigt traverse la rangee entiere sans buter. Seuls les deux ecrans
    qui encadrent la pastille sont montes, donc traverser trois onglets ne
    coute pas trois ecrans a l'image en meme temps.
  */
  const grab = useRef<{ x: number; gauche: number; step: number; moved: boolean } | null>(null);

  /*
    La respiration.

    Des qu'on touche la barre — un appui, un glissement, un changement
    d'onglet — elle enfle d'un rien, puis revient a sa taille des qu'on la
    laisse. Elle repond au doigt au lieu de rester un decor, et le
    mouvement dit que le geste est pris en compte avant meme que l'ecran
    change.

    Le retour se declenche a l'arret, pas a la levee du doigt : pendant
    qu'on se promene dans la rangee, la barre reste gonflee.
  */
  const [respire, setRespire] = useState(false);
  const souffle = useRef(0);

  const respirer = useCallback(() => {
    setRespire(true);
    window.clearTimeout(souffle.current);
    /*
      Un filet, pas un delai.

      La barre se degonfle quand le doigt se leve, et c'est tout. Ce
      minuteur ne sert qu'au cas ou la levee n'arrive jamais — un geste
      interrompu par un appel, une capture de pointeur perdue — pour
      qu'elle ne reste pas gonflee indefiniment.
    */
    souffle.current = window.setTimeout(() => setRespire(false), 4000);
  }, []);

  /* Le doigt se leve : elle redescend tout de suite, en douceur. */
  const souffler = useCallback(() => {
    window.clearTimeout(souffle.current);
    setRespire(false);
  }, []);

  useEffect(() => () => window.clearTimeout(souffle.current), []);

  function onPillDown(event: PointerEvent<HTMLAnchorElement>) {
    if (active === -1 || nav.drag) return;

    const rangee = event.currentTarget.parentElement;
    if (!rangee) return;
    event.currentTarget.setPointerCapture(event.pointerId);

    /*
      La geometrie de la rangee, pas celle de l'icone : c'est en absolu
      que le doigt designe un onglet. Le remplissage lateral est repris de
      la meme facon que la pastille le calcule en CSS.
    */
    const boite = rangee.getBoundingClientRect();
    const pad = Number.parseFloat(getComputedStyle(rangee).paddingLeft) || 0;
    const step = (boite.width - pad * 2) / onglets.length || 1;

    /* L'origine de la rangee : son bord gauche, ou le droit en arabe. */
    const origine = sensX === 1 ? boite.left + pad : boite.right - pad;
    grab.current = { x: event.clientX, gauche: origine, step, moved: false };
    respirer();
  }

  function onPillMove(event: PointerEvent<HTMLAnchorElement>) {
    const held = grab.current;
    if (!held) return;

    if (!held.moved) {
      // Quelques pixels avant de decider : un appui tremble toujours un peu.
      if (Math.abs(event.clientX - held.x) < 6) return;
      // Toute la rangee est ouverte : le doigt ne bute sur aucune icone.
      if (!nav.begin([0, onglets.length - 1])) {
        grab.current = null;
        return;
      }
      held.moved = true;
    }

    // La case sous le doigt, centre compris : le milieu d'une case vaut
    // son indice entier.
    nav.moveTo(((event.clientX - held.gauche) * sensX) / held.step - 0.5);
    respirer();
  }

  function onPillUp() {
    const held = grab.current;
    if (!held) return;
    grab.current = null;
    if (held.moved) nav.end();
    souffler();
  }

  /*
    Un glissement n'est pas un clic. Sans cela, lever le doigt renvoie
    aussi vers l'onglet qu'on vient de quitter, et le geste s'annule tout
    seul a l'instant ou il aboutit.
  */
  function onPillClick(event: MouseEvent<HTMLAnchorElement>) {
    if (nav.drag) {
      event.preventDefault();
      return;
    }
    /*
      Rien a gonfler ici : l'appui l'a deja fait, et la levee vient de la
      degonfler. Rappeler `respirer()` apres coup la rallumait pour rien —
      c'est ce qui donnait ce delai avant qu'elle retombe.
    */

    /*
      Deja sur cet onglet : on remonte au lieu de ne rien faire.

      L'adresse complete, et non le seul chemin : depuis un fil filtre
      — `/app/feed?q=rooftop` —, toucher l'onglet doit d'abord ramener au
      fil entier. C'est une fois revenu dessus, l'adresse nue, que le
      meme geste remonte.
    */
    const cible = event.currentTarget.getAttribute('href');
    if (cible !== emplacement.pathname || emplacement.search) return;
    event.preventDefault();
    remonterLOnglet();
  }

  return (
    <nav className="tabbar" aria-label={t.composants.barre.navigation}>
      <div
        className={`tabbar__inner${respire ? ' tabbar__inner--respire' : ''}`}
        style={{ '--tabs': onglets.length } as CSSProperties}
      >
        {/*
          La pastille de l'onglet actif. Hors du flux, posee sous les
          icones : elle glisse d'une case a l'autre au lieu de disparaitre
          d'un cote pour reapparaitre de l'autre. Absente hors des
          onglets — un sous-ecran n'en allume aucun.
        */}
        {active >= 0 && (
          <span
            className={`tabbar__pill${nav.drag && !nav.drag.settling ? ' tabbar__pill--held' : ''}`}
            /* Sa case, ou la position continue du geste en cours. */
            style={{ '--at': nav.drag ? nav.drag.pos : active } as CSSProperties}
            aria-hidden="true"
          />
        )}

        {onglets.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `tabbar__item${isActive ? ' tabbar__item--on' : ''}`}
            /* N'importe quelle icone tient le geste : on se promene
               depuis la ou le doigt s'est pose. */
            onPointerDown={onPillDown}
            onPointerMove={onPillMove}
            onPointerUp={onPillUp}
            onPointerCancel={onPillUp}
            onClick={onPillClick}
            aria-label={to === '/app/messages' && aVoir ? t.composants.barre.aRegarder(label, aVoir) : label}
          >
            {({ isActive }) => (
              <span className="tabbar__slot">
                <Icon size={26} weight={isActive ? 'fill' : 'regular'} />
                {/* Ce qui attend derriere l'onglet, quand on n'y est pas. */}
                {!isActive &&
                  ((to === '/app/messages' && Boolean(aVoir)) || (to === '/app/feed' && activite > 0)) && (
                    <span className="pastille tabbar__pastille" aria-hidden="true" />
                  )}
              </span>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
